import type { QuoteBuilderApi } from '../api/client'
import type { Quote, QuoteLine } from '../api/types'

// The demo's quote edit queue (`quote-api.ts`): every edit shows at once, saves in order against
// the latest revision, replays once after a stale-revision 412, and reports failures per edit so
// the field that failed can offer a retry. Equipment and Pricing share it.

export type QuoteEdit = {
  key: string
  kind?: 'alternatives'
  optimistic: (quote: Quote) => Quote
  persist: (quote: Quote) => Promise<Quote>
  /** Milliseconds to wait for further typing before saving (quantity, notes). */
  delay?: number
}

type PendingQuoteEdit = QuoteEdit & { readyAt: number }

export type QuoteMutationFailure = { edit: QuoteEdit; error: Error }

export type LineChanges = Partial<QuoteLine>

/** The staff line endpoints the editor calls (demo `quoteApi`, re-rooted; the actor is the signed-in user). */
export function quoteLineApi(api: QuoteBuilderApi) {
  const send = (path: string, method: string, quote: Quote, body: Record<string, unknown>) =>
    api.request<Quote>(path, {
      method,
      body: JSON.stringify({ expected_revision: quote.revision, ...body }),
    })
  return {
    read: (quoteId: string) => api.request<Quote>(`/quotes/${quoteId}`),
    addLine: (quote: Quote, line: Record<string, unknown>) => send(`/quotes/${quote.id}/lines`, 'POST', quote, line),
    updateLine: (quote: Quote, lineId: string, changes: Record<string, unknown>) =>
      send(`/quotes/${quote.id}/lines/${lineId}`, 'PATCH', quote, changes),
    replaceLine: (quote: Quote, lineId: string, productId: number) =>
      send(`/quotes/${quote.id}/lines/${lineId}/replace`, 'POST', quote, {
        product_id: productId,
      }),
    setAlternatives: (quote: Quote, lineId: string, productIds: number[]) =>
      send(`/quotes/${quote.id}/lines/${lineId}/alternatives`, 'PUT', quote, {
        product_ids: productIds,
      }),
    removeLine: (quote: Quote, lineId: string) =>
      api.request<Quote>(`/quotes/${quote.id}/lines/${lineId}?expected_revision=${quote.revision}`, {
        method: 'DELETE',
      }),
  }
}

export type QuoteLineApi = ReturnType<typeof quoteLineApi>

let optimisticSequence = 0
export function optimisticLineId() {
  return `optimistic-${++optimisticSequence}`
}

function decimal(value: unknown) {
  const parsed = Number(value ?? 0)
  return Number.isFinite(parsed) ? parsed : 0
}

function amount(value: number) {
  return value.toFixed(2)
}

function moneyNumber(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100
}

function calculateLine(line: QuoteLine): QuoteLine {
  const quantity = decimal(line.quantity)
  const netUnit = decimal(line.derived_net_amount ?? line.source_net_amount ?? line.list_amount)
  const sellUnit = decimal(line.sell_amount ?? line.list_amount)
  const net = moneyNumber(quantity * netUnit)
  const sell = moneyNumber(quantity * sellUnit)
  const profit = moneyNumber(sell - net)
  return {
    ...line,
    totals: {
      list: amount(moneyNumber(quantity * decimal(line.list_amount))),
      net: amount(net),
      sell: amount(sell),
      profit: amount(profit),
      margin_percent: amount(sell ? (profit / sell) * 100 : 0),
      map_warning: line.map_amount != null && sellUnit < decimal(line.map_amount),
    },
  }
}

/** Recompute totals while edits are in flight; tax stays server-owned (demo `calculateQuote`). */
export function calculateQuote(quote: Quote): Quote {
  const lines = quote.lines.map(calculateLine)
  const sum = (key: 'list' | 'net' | 'sell') => lines.reduce((total, line) => total + decimal(line.totals[key]), 0)
  const sell = sum('sell')
  const net = sum('net')
  const rebate = Math.min(moneyNumber(decimal(quote.rebate_amount)), sell)
  const taxable = moneyNumber(sell - rebate)
  const profit = moneyNumber(taxable - net)
  const tax = moneyNumber(decimal(quote.totals.tax))
  const freight = moneyNumber(decimal(quote.freight_amount))
  const grandTotal = moneyNumber(taxable + tax + freight)
  const deposit = moneyNumber((grandTotal * decimal(quote.deposit_percent)) / 100)
  return {
    ...quote,
    lines,
    totals: {
      ...quote.totals,
      list: amount(sum('list')),
      net: amount(net),
      sell: amount(sell),
      profit: amount(profit),
      margin_percent: amount(taxable ? (profit / taxable) * 100 : 0),
      map_warning: lines.some((line) => line.totals.map_warning),
      rebate: amount(rebate),
      taxable_subtotal: amount(taxable),
      tax: amount(tax),
      freight: amount(freight),
      grand_total: amount(grandTotal),
      deposit: amount(deposit),
      balance: amount(moneyNumber(grandTotal - deposit)),
    },
  }
}

/** A line and everything beneath it, in item order. */
export function lineFamily(lines: QuoteLine[], line: QuoteLine): QuoteLine[] {
  const visit = (item: QuoteLine): QuoteLine[] => [
    item,
    ...lines.filter((candidate) => candidate.parent_line_id === item.id).flatMap(visit),
  ]
  return visit(line)
}

/** The lines with a line and its sub-lines moved together to `position`, as the server moves them. */
export function moveLineFamily(lines: QuoteLine[], lineId: string, position: number): QuoteLine[] {
  const line = lines.find((candidate) => candidate.id === lineId)
  if (!line) return lines
  const family = lineFamily(lines, line)
  const moving = new Set(family.map((item) => item.id))
  const ordered = lines.filter((candidate) => !moving.has(candidate.id))
  ordered.splice(Math.max(0, Math.min(position - 1, ordered.length)), 0, ...family)
  return ordered
}

export function optimisticLineUpdate(quote: Quote, lineId: string, changes: LineChanges): Quote {
  const lines = quote.lines.map((line) => (line.id === lineId ? { ...line, ...changes } : line))
  if (changes.position !== undefined) {
    const line = lines.find((candidate) => candidate.id === lineId)
    if (line) {
      const ordered = moveLineFamily(lines, lineId, changes.position)
      return calculateQuote({
        ...quote,
        lines: ordered.map((candidate, index) => ({
          ...candidate,
          position: index + 1,
        })),
      })
    }
  }
  return calculateQuote({ ...quote, lines })
}

export function optimisticLineInsert(quote: Quote, line: QuoteLine, position = quote.lines.length + 1): Quote {
  const lines = [...quote.lines]
  lines.splice(Math.max(0, Math.min(position - 1, lines.length)), 0, line)
  return calculateQuote({
    ...quote,
    lines: lines.map((candidate, index) => ({
      ...candidate,
      position: index + 1,
    })),
  })
}

/** Remove a line and everything beneath it. */
export function optimisticLineRemove(quote: Quote, lineId: string): Quote {
  const removed = new Set([lineId])
  let size = 0
  while (size !== removed.size) {
    size = removed.size
    for (const line of quote.lines) if (line.parent_line_id && removed.has(line.parent_line_id)) removed.add(line.id)
  }
  return calculateQuote({
    ...quote,
    lines: quote.lines.filter((line) => !removed.has(line.id)).map((line, index) => ({ ...line, position: index + 1 })),
  })
}

/** The create body that copies a line (Duplicate). */
export function lineCreateBody(line: QuoteLine, position = line.position) {
  if (line.product_id) {
    return {
      line_type: line.line_type,
      product_id: line.product_id,
      parent_line_id: line.parent_line_id,
      position,
      quantity: line.quantity,
      description: line.description,
      sell_amount: line.sell_amount,
      configuration: line.configuration,
      notes: line.notes,
    }
  }
  return {
    line_type: line.line_type,
    parent_line_id: line.parent_line_id,
    position,
    quantity: line.quantity,
    description: line.description,
    sell_amount: line.sell_amount || line.list_amount,
    list_amount: line.list_amount,
    map_amount: line.map_amount,
    source_net_amount: line.source_net_amount || line.list_amount,
    notes: line.notes,
  }
}

export class QuoteMutationClient {
  private serverQuote: Quote
  private active: QuoteEdit | null = null
  private pending: PendingQuoteEdit[] = []
  private timer: ReturnType<typeof setTimeout> | null = null
  private disposed = false
  private readonly readLatest: () => Promise<Quote>
  private readonly onChange: (quote: Quote, pendingCount: number) => void
  private readonly onFailure: (failure: QuoteMutationFailure) => void
  private readonly onPersisted: (quote: Quote) => void

  constructor(
    quote: Quote,
    readLatest: () => Promise<Quote>,
    onChange: (quote: Quote, pendingCount: number) => void,
    onFailure: (failure: QuoteMutationFailure) => void,
    onPersisted: (quote: Quote) => void,
  ) {
    this.serverQuote = quote
    this.readLatest = readLatest
    this.onChange = onChange
    this.onFailure = onFailure
    this.onPersisted = onPersisted
  }

  enqueue(edit: QuoteEdit) {
    const pending = { ...edit, readyAt: Date.now() + (edit.delay ?? 0) }
    const existing = this.pending.findIndex((candidate) => candidate.key === edit.key)
    if (existing >= 0) this.pending[existing] = pending
    else this.pending.push(pending)
    this.emit()
    this.schedule()
  }

  get isDisposed() {
    return this.disposed
  }

  replaceServerQuote(quote: Quote) {
    if (quote.id !== this.serverQuote.id || quote.revision < this.serverQuote.revision) return
    this.serverQuote = quote
    this.emit()
  }

  dispose() {
    this.disposed = true
    if (this.timer) clearTimeout(this.timer)
  }

  private visibleQuote() {
    const edits = [...(this.active ? [this.active] : []), ...this.pending]
    return edits.reduce((quote, edit) => edit.optimistic(quote), this.serverQuote)
  }

  private emit() {
    if (!this.disposed) this.onChange(this.visibleQuote(), this.pending.length + Number(Boolean(this.active)))
  }

  private schedule() {
    if (this.disposed || this.active) return
    if (this.timer) clearTimeout(this.timer)
    const next = this.pending.reduce<PendingQuoteEdit | null>(
      (candidate, edit) => (!candidate || edit.readyAt < candidate.readyAt ? edit : candidate),
      null,
    )
    if (!next) return
    this.timer = setTimeout(
      () => {
        this.timer = null
        void this.flushReady()
      },
      Math.max(0, next.readyAt - Date.now()),
    )
  }

  private async flushReady() {
    if (this.disposed || this.active) return
    const index = this.pending.findIndex((edit) => edit.readyAt <= Date.now())
    if (index < 0) {
      this.schedule()
      return
    }
    const edit = this.pending.splice(index, 1)[0]
    if (!edit) return
    this.active = edit
    this.emit()
    try {
      let next: Quote
      try {
        next = await edit.persist(this.serverQuote)
      } catch (error) {
        if (typeof error !== 'object' || error === null || !('status' in error) || error.status !== 412) throw error
        this.replaceServerQuote(await this.readLatest())
        next = await edit.persist(this.serverQuote)
      }
      // A late response settles this edit but never overwrites a newer external update.
      this.active = null
      if (next.id === this.serverQuote.id && next.revision >= this.serverQuote.revision) {
        this.serverQuote = next
        this.onPersisted(next)
      }
      this.emit()
    } catch (caught) {
      this.active = null
      this.onFailure({
        edit,
        error: caught instanceof Error ? caught : new Error('The quote could not be updated.'),
      })
      this.emit()
    } finally {
      this.schedule()
    }
  }
}
